import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/utils";
import TestimonialsSections from "@/components/site/TestimonialsSections";
import { MeditationCheckoutHost } from "@/components/site/MeditationCheckout";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { inr, themeWord, scrollToId, meditationActions as act, PreviewButton, SectionHead } from "@/components/site/MeditationBits";

const Eyebrow = ({ children }) => <p className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-terracotta">{children}</p>;

function Hero({ meds, col }) {
  const prices = [...new Set(meds.map(m => Number(m.price_inr)))];
  const each = prices.length === 1 ? `${inr(prices[0])} each` : `from ${inr(Math.min(...prices))}`;
  return (
    <section className="container-ic pt-14 pb-12 sm:pt-20 sm:pb-16" data-testid="med-hero">
      <Eyebrow>Meditations Collection</Eyebrow>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2422] leading-[1.08] mt-4 max-w-3xl">Find the meditation you need today.</h1>
      <p className="mt-5 text-xs sm:text-sm tracking-[0.3em] text-[#4A3B32] font-medium">{meds.map(m => m.theme).join("  ·  ")}</p>
      <p className="mt-6 text-lg sm:text-xl text-[#3D3633] leading-relaxed max-w-2xl font-light">Four guided meditations. Four different ways of turning inward. Whether you want to return to the present, reconnect with joy, bring consciousness to an intention, or explore deeper stillness, begin with what you need today.</p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
        <button onClick={() => scrollToId("what-you-need")} className="btn-ghost !py-3.5" data-testid="hero-find-my-meditation">Find My Meditation <ArrowDown size={16} className="ml-2" /></button>
        {col && <button onClick={() => act.buyCollection(col)} className="btn-primary !py-3.5" data-testid="hero-get-all-four">Get All Four — {inr(col.price_inr)}</button>}
      </div>
      {meds.length > 0 && <p className="mt-4 text-sm text-[#7E736E]" data-testid="hero-price-note">Individual meditations {each}  ·  One-time purchase</p>}
    </section>
  );
}

function NeedCard({ m }) {
  return (
    <div className="soft-card !p-6 sm:!p-7 flex flex-col" data-testid={`need-card-${m.slug}`}>
      <Eyebrow>{m.theme}</Eyebrow>
      <h3 className="font-serif text-2xl text-[#2D2422] mt-2 leading-tight">{m.name}</h3>
      <p className="text-sm italic text-[#7E736E] mt-1">{m.subtitle}</p>
      <p className="mt-3 text-[15px] text-[#3D3633] leading-relaxed flex-1">{m.short_line}</p>
      <button onClick={() => scrollToId(`med-${m.slug}`)} className="mt-5 self-start inline-flex items-center gap-2 text-sm font-medium text-terracotta hover:gap-3 transition-[gap]" data-testid={`explore-theme-${m.slug}`}>
        Explore {themeWord(m.theme)} <ArrowRight size={15} />
      </button>
    </div>
  );
}

function DetailCard({ m, i }) {
  const img = resolveImg(m.image_url);
  return (
    <article id={`med-${m.slug}`} className="scroll-mt-24 soft-card !p-0 overflow-hidden grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" data-testid={`detail-card-${m.slug}`}>
      <div className={`relative bg-[#F3EFEA] min-h-[180px] md:min-h-full ${i % 2 ? "md:order-2" : ""}`}>
        {img ? <img src={img} alt={m.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-testid={`detail-image-${m.slug}`} />
          : <div className="absolute inset-0 flex items-center justify-center"><span className="font-serif text-5xl sm:text-6xl text-[#E07A5F]/25 tracking-wide">{themeWord(m.theme)}</span></div>}
      </div>
      <div className="p-6 sm:p-10">
        <Eyebrow>{m.theme}</Eyebrow>
        <h3 className="font-serif text-3xl sm:text-4xl text-[#2D2422] mt-2 leading-tight">{m.name}</h3>
        <p className="italic text-[#7E736E] mt-1">{m.subtitle}</p>
        <p className="mt-4 text-lg text-[#3D3633] leading-relaxed">{m.short_line}</p>
        <p className="mt-3 text-[15px] text-[#5C5955] leading-relaxed">{m.description}</p>
        <p className="mt-4 font-serif text-xl italic text-[#4A3B32] leading-snug">{m.headline}</p>
        <p className="mt-5 text-sm font-medium text-[#4A3B32]" data-testid={`meta-${m.slug}`}>{[inr(m.price_inr), m.duration, m.language].filter(Boolean).join("  ·  ")}</p>
        <div className="mt-5"><PreviewButton m={m} /></div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => act.buy(m)} className="btn-primary" data-testid={`begin-${m.slug}`}>Begin — {inr(m.price_inr)}</button>
          <Link to={`/meditations/${m.slug}`} className="btn-ghost" data-testid={`explore-meditation-${m.slug}`}>Explore Meditation</Link>
        </div>
      </div>
    </article>
  );
}

function CollectionSection({ meds, col }) {
  return (
    <section id="complete-collection" className="scroll-mt-24 bg-[#4A3B32] text-[#FDFBF7]" data-testid="collection-section">
      <div className="container-ic py-16 sm:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="collection-name">{col.name}</h2>
          <p className="mt-4 text-lg sm:text-xl text-[#F3C9B9] font-light">{col.description}</p>
          <p className="mt-6 text-[15px] sm:text-base text-[#EBE5D9]/85 leading-relaxed">You may not need the same meditation every day. Some days call for Presence. Some call for Joy. Some call for Intention. And sometimes you may feel drawn towards deeper Stillness and Oneness.</p>
          <p className="mt-4 text-[15px] sm:text-base text-[#EBE5D9]/85 leading-relaxed">{col.name} gives you all four practices so you can choose according to what you need.</p>
        </div>
        <div className="rounded-3xl bg-[#5C4A3F]/60 border border-white/10 p-6 sm:p-8">
          <ul className="grid grid-cols-2 gap-3">
            {meds.map(m => (
              <li key={m.id} className="rounded-2xl bg-white/5 p-4">
                <div className="text-[10px] tracking-[0.25em] text-[#F3C9B9]">{m.theme}</div>
                <div className="font-serif text-lg mt-1 leading-tight">{m.name}</div>
              </li>
            ))}
          </ul>
          <div className="mt-6 space-y-1.5 text-sm">
            <div className="flex justify-between text-[#EBE5D9]/80"><span>Individual price:</span><span className="line-through" data-testid="collection-combined-value">{inr(col.combined_value_inr)}</span></div>
            <div className="flex justify-between text-lg font-semibold"><span>Complete Collection:</span><span data-testid="collection-price">{inr(col.price_inr)}</span></div>
          </div>
          <button onClick={() => act.buyCollection(col)} className="btn-primary w-full mt-6 !py-3.5" data-testid="collection-get-all-four">Get All Four — {inr(col.price_inr)}</button>
        </div>
      </div>
    </section>
  );
}

function ChooseCard({ m }) {
  return (
    <div className="soft-card !p-6 flex flex-col" data-testid={`choose-card-${m.slug}`}>
      <Eyebrow>{m.theme}</Eyebrow>
      <h3 className="font-serif text-2xl text-[#2D2422] mt-2 leading-tight">{m.name}</h3>
      <p className="mt-2 text-[15px] text-[#3D3633] leading-relaxed flex-1">{m.short_line}</p>
      <button onClick={() => act.buy(m)} className="btn-primary mt-5 w-full sm:w-auto sm:self-start" data-testid={`choose-${m.slug}`}>Choose {themeWord(m.theme)} — {inr(m.price_inr)}</button>
    </div>
  );
}

const faqs = (meds, col) => {
  const prices = [...new Set(meds.map(m => Number(m.price_inr)))];
  const one = prices.length > 1 ? `from ${inr(Math.min(...prices))}` : prices.length ? inr(prices[0]) : "";
  return [
    ["Which meditation should I begin with?", "Start with what feels most relevant to you today. Choose Presence to practise returning your attention to the present. Choose Joy to reconnect with warmth and joy within you. Choose Intention to bring a personally chosen Sankalp into meditation. Choose Oneness if you feel drawn towards deeper stillness and the exploration of Shunya."],
    ["Do I need previous meditation experience?", "No. The meditations are guided, so you can simply follow the instructions."],
    ["Do I have to practise the four meditations in order?", "No. Each meditation can be practised independently."],
    ["What is a Sankalp?", "A Sankalp is a consciously chosen positive intention — something you wish to cultivate, embody or change in your life."],
    ["Is Aekam only for experienced meditators?", "No. Aekam explores a deeper dimension of meditation, but no previous experience is required. Follow the guidance without trying to force a particular experience."],
    ["Can I buy just one meditation?", `Yes. Each meditation is available individually for ${one}${col ? `, or you can choose all four for ${inr(col.price_inr)}` : ""}.`],
    ["Is this a subscription?", "No. The listed price is a one-time payment."],
  ];
};

function Experiences() {
  return (
    <section className="container-ic py-16 sm:py-20 space-y-16">
      <div data-testid="experiences-section">
        <SectionHead title="Experiences with iChaitanya" sub="Hear from people who have experienced iChaitanya meditation and guided sessions." />
        <div className="mt-8"><TestimonialsSections preview limit={2} showStatic={false} /></div>
        <Link to="/testimonials" className="btn-ghost mt-8" data-testid="view-more-experiences">View More Experiences <ArrowRight size={15} className="ml-2" /></Link>
      </div>
      <div data-testid="session-video-section">
        <Eyebrow>Experience iChaitanya</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2D2422] leading-tight mt-3">See meditation in practice.</h2>
        <p className="mt-3 text-base sm:text-lg text-[#7E736E] font-light">A glimpse inside an iChaitanya guided meditation session.</p>
        <div className="mt-8 aspect-video max-w-3xl rounded-3xl bg-[#F3EFEA] border border-[#EBE5D9] flex flex-col items-center justify-center text-[#9C928D]" data-testid="session-video-placeholder">
          <PlayCircle size={44} strokeWidth={1.2} />
          <span className="mt-3 text-sm">Session video coming soon</span>
        </div>
      </div>
    </section>
  );
}

export default function MeditationsPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/meditations").then(r => setData(r.data)).catch(() => setData({ meditations: [], collection: null })); }, []);
  if (!data) return <div className="min-h-[60vh]" />;
  const meds = data.meditations, col = data.collection;
  return (
    <div className="fade-up overflow-x-hidden" data-testid="page-meditations">
      <Hero meds={meds} col={col} />

      <section className="container-ic pb-16 sm:pb-20">
        <SectionHead id="what-you-need" title="What do you need right now?" />
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">{meds.map(m => <NeedCard key={m.id} m={m} />)}</div>
        <div className="mt-10 soft-card bg-[#F3EFEA] !p-6 sm:!p-10" data-testid="not-sure-box">
          <h3 className="font-serif text-2xl sm:text-3xl text-[#2D2422]">Not sure which one to choose?</h3>
          <p className="mt-3 text-[15px] sm:text-base text-[#3D3633] leading-relaxed max-w-3xl">There is no wrong place to begin. Start with the experience that feels most relevant to you today — or choose the complete collection and return to a different practice as your needs change.</p>
          {col && <button onClick={() => scrollToId("complete-collection")} className="btn-ghost mt-6" data-testid="explore-complete-collection">Explore the Complete Collection</button>}
        </div>
      </section>

      <section className="container-ic pb-16 sm:pb-24">
        <SectionHead id="choose-your-meditation" title="Choose Your Meditation" />
        <div className="mt-8 space-y-6 sm:space-y-8">{meds.map((m, i) => <DetailCard key={m.id} m={m} i={i} />)}</div>
      </section>

      {col && <CollectionSection meds={meds} col={col} />}

      <Experiences />

      <section className="container-ic pb-16 sm:pb-20" data-testid="never-meditated-section">
        <div className="soft-card !p-6 sm:!p-12 max-w-4xl">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2422]">Never meditated before?</h2>
          <p className="font-serif text-2xl italic text-terracotta mt-1">You can start here.</p>
          <p className="mt-5 text-[15px] sm:text-base text-[#3D3633] leading-relaxed">You don't need perfect concentration. You don't need to stop every thought. You don't need previous meditation experience. Choose the meditation that speaks to what you need today and simply follow the guidance. When your attention wanders, notice it and return. That returning is part of meditation.</p>
        </div>
      </section>

      <section className="container-ic pb-16 sm:pb-24" data-testid="faq-section">
        <SectionHead title="Frequently Asked Questions" />
        <Accordion type="single" collapsible className="mt-6 max-w-3xl">
          {faqs(meds, col).map(([q, a], i) => (
            <AccordionItem key={i} value={`f${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-serif text-lg sm:text-xl text-[#2D2422] hover:no-underline py-5">{q}</AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#3D3633] leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="container-ic pb-16 sm:pb-20">
        <SectionHead id="what-you-need-today" title="What do you need today?" />
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">{meds.map(m => <ChooseCard key={m.id} m={m} />)}</div>
      </section>

      {col && (
        <section className="container-ic pb-20 sm:pb-28" data-testid="final-collection">
          <p className="font-serif text-2xl sm:text-3xl italic text-[#7E736E]">Or experience all four.</p>
          <div className="mt-6 rounded-3xl bg-[#4A3B32] text-[#FDFBF7] p-7 sm:p-12 flex flex-col lg:flex-row lg:items-center gap-6 lg:justify-between">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{col.name}</h2>
              <p className="mt-3 text-xs sm:text-sm tracking-[0.3em] text-[#F3C9B9]">{meds.map(m => m.theme).join("  ·  ")}</p>
              <p className="mt-4 text-2xl"><span className="line-through text-[#EBE5D9]/60 mr-3" data-testid="final-combined-value">{inr(col.combined_value_inr)}</span><span className="font-semibold" data-testid="final-price">{inr(col.price_inr)}</span></p>
            </div>
            <button onClick={() => act.buyCollection(col)} className="btn-primary !py-4 !px-9 w-full lg:w-auto" data-testid="final-get-all-four">Get All Four — {inr(col.price_inr)}</button>
          </div>
        </section>
      )}
      <MeditationCheckoutHost includes={meds.map(m => m.name)} />
    </div>
  );
}
